/**
 * product-page.js
 *
 * Handles all interactive behaviour on product detail pages
 * (templates/product.liquid, templates/product.filter.liquid).
 *
 * Features:
 *   1. Gallery — clicking a thumbnail swaps the main image
 *   2. Quantity stepper — +/− buttons update the number input
 *   3. Variant switching — selecting options updates the hidden variant id input
 *      and the price display without a page reload
 *   4. Ajax add-to-cart — submitting the form calls window.addToCart(),
 *      shows button feedback, dispatches a cart:update event so any
 *      cart-count badges on the page stay in sync, and does NOT reload the page
 */

(function () {
  'use strict';

  // ─── Locale helpers ────────────────────────────────────────────────────────

  const isFr = () => document.documentElement.lang?.startsWith('fr');

  function t(keyFr, keyEn) {
    return isFr() ? keyFr : keyEn;
  }

  // ─── Money formatting ──────────────────────────────────────────────────────

  /**
   * Format a Shopify price integer (cents) using the store's money format.
   * Falls back to a plain "$X.XX" if window.theme.moneyFormat isn't set.
   *
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const moneyFormat = window.theme?.moneyFormat;

    if (!moneyFormat) return `$${amount}`;

    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
  }

  // ─── Variant map ───────────────────────────────────────────────────────────

  /**
   * Parse the variants JSON embedded in the page.
   * Returns a Map keyed by variant id.
   *
   * @returns {Map<number, {id: number, price: number, available: boolean}>}
   */
  function buildVariantMap() {
    const el = document.getElementById('product-variants-json');
    if (!el) return new Map();
    try {
      const variants = JSON.parse(el.textContent || '[]');
      return new Map(variants.map((v) => [v.id, v]));
    } catch (e) {
      console.error('[product-page] Failed to parse variant JSON', e);
      return new Map();
    }
  }

  /**
   * Given the current option select values, find the matching variant.
   * Works for products with 1–3 option dimensions.
   *
   * @param {Map} variantMap
   * @param {string[]} selectedOptions  e.g. ['Red', 'Large']
   * @param {Array}    allVariants       raw array from JSON
   * @returns {{ id: number, price: number, available: boolean } | undefined}
   */
  function findVariant(allVariants, selectedOptions) {
    return allVariants.find((v) => {
      return selectedOptions.every((val, i) => v[`option${i + 1}`] === val);
    });
  }

  // ─── Gallery ───────────────────────────────────────────────────────────────

  /**
   * Initialise the image gallery.
   *
   * - Orphan thumbnails (no `data-variant-ids`) are always visible.
   * - Variant thumbnails are shown only when their variant is selected.
   * - The main image always reflects the currently selected variant.
   *
   * @returns {((variantId: number|string) => void) | null}
   *   A function that callers (e.g. variant switching) can invoke to sync the
   *   gallery whenever the selected variant changes.  Returns null if the
   *   gallery markup is absent.
   */
  function initGallery() {
    const mainImage = /** @type {HTMLImageElement | null} */ (document.getElementById('product-main-image'));
    if (!mainImage) return null;

    // Pre-sized image URLs per variant, emitted by Liquid into the page.
    let variantImages = {};
    try {
      const el = document.getElementById('product-variant-images-json');
      variantImages = JSON.parse(el?.textContent || '{}');
    } catch (e) {
      console.error('[product-page] Failed to parse variant images JSON', e);
    }

    const thumbItems = /** @type {NodeListOf<HTMLLIElement>} */ (
      document.querySelectorAll('._thumbnails > li')
    );

    function setActiveThumb(btn) {
      document.querySelectorAll('._thumbnail-btn').forEach((b) => b.classList.remove('_thumbnail-btn--active'));
      if (btn) btn.classList.add('_thumbnail-btn--active');
    }

    /**
     * Swap the main image and update thumbnail visibility for `variantId`.
     * Orphan thumbnails (no data-variant-ids) are never touched.
     *
     * @param {number|string} variantId
     */
    function updateGalleryForVariant(variantId) {
      const id = String(variantId);
      const imgData = variantImages[id];

      if (imgData?.src) {
        mainImage.src = imgData.src;
        mainImage.alt = imgData.alt || '';
      }

      let activeBtn = null;
      thumbItems.forEach((li) => {
        const ids = li.dataset.variantIds;
        if (!ids) return; // orphan — always visible, never hidden

        const isMatch = ids.split(',').filter(Boolean).includes(id);
        li.hidden = !isMatch;
        if (isMatch) activeBtn = li.querySelector('._thumbnail-btn');
      });

      if (activeBtn) setActiveThumb(activeBtn);
    }

    // Thumbnail click: swap main image and move active class.
    thumbItems.forEach((li) => {
      const btn = /** @type {HTMLButtonElement | null} */ (li.querySelector('._thumbnail-btn'));
      if (!btn) return;
      btn.addEventListener('click', () => {
        const src = btn.dataset.imageSrc;
        const alt = btn.dataset.imageAlt || '';
        if (src) {
          mainImage.src = src;
          mainImage.alt = alt;
        }
        setActiveThumb(btn);
      });
    });

    // Sync gallery with the variant that Liquid pre-selected on the server.
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('product-variant-id')
    );
    if (variantIdInput?.value) {
      updateGalleryForVariant(variantIdInput.value);
    }

    return updateGalleryForVariant;
  }

  // ─── Quantity stepper ──────────────────────────────────────────────────────

  function initQuantityStepper() {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById('product-quantity'));
    if (!input) return;

    document.querySelector('._qty-minus')?.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 1;
      if (current > 1) input.value = String(current - 1);
    });

    document.querySelector('._qty-plus')?.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 1;
      input.value = String(current + 1);
    });
  }

  // ─── Variant switching ─────────────────────────────────────────────────────

  /**
   * @param {((variantId: number|string) => void) | null} onVariantChange
   *   Optional callback invoked with the new variant's ID whenever the
   *   selection resolves to a known variant (used to sync the gallery).
   */
  function initVariantSwitching(onVariantChange) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (document.getElementById('product-variant-id'));
    const priceEl = document.getElementById('product-price');
    const addBtn = /** @type {HTMLButtonElement | null} */ (document.getElementById('product-add-btn'));
    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      document.querySelectorAll('#product-add-form select[data-option-position]')
    );

    if (!variantIdInput || !selects.length) return;

    // Build a raw array (needed for option1/option2/option3 lookup)
    const variantMapEl = document.getElementById('product-variants-json');
    let allVariants = [];
    try {
      allVariants = JSON.parse(variantMapEl?.textContent || '[]');
    } catch (_) {}

    function update() {
      const selectedOptions = Array.from(selects).map((s) => s.value);

      // Only search when all options are chosen
      if (selectedOptions.some((v) => !v)) return;

      const match = allVariants.find((v) =>
        selectedOptions.every((val, i) => String(v[`option${i + 1}`]) === String(val))
      );

      if (!match) return;

      // Update hidden variant id
      variantIdInput.value = String(match.id);

      // Sync gallery image + thumbnail visibility
      if (typeof onVariantChange === 'function') onVariantChange(match.id);

      // Update price display
      if (priceEl) {
        priceEl.textContent = formatMoney(match.price);
      }

      // Disable add button if unavailable
      if (addBtn) {
        addBtn.disabled = !match.available;
        if (!match.available) {
          addBtn.textContent = t('Épuisé', 'Sold out');
        } else {
          addBtn.textContent = t(
            addBtn.dataset.labelAddFr || 'Ajouter au panier',
            addBtn.dataset.labelAddEn || 'Add to cart'
          );
        }
      }
    }

    selects.forEach((select) => select.addEventListener('change', update));

    // Run once on load to sync any server-side pre-selection
    update();
  }

  // ─── Ajax add-to-cart ──────────────────────────────────────────────────────

  function initAjaxAddToCart() {
    const form = document.getElementById('product-add-form');
    const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById('product-add-btn'));

    if (!form || !btn) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const variantId = /** @type {HTMLInputElement | null} */ (
        document.getElementById('product-variant-id')
      )?.value;
      const quantity = parseInt(
        /** @type {HTMLInputElement | null} */ (document.getElementById('product-quantity'))?.value || '1',
        10
      );

      if (!variantId) {
        alert(t('Veuillez sélectionner toutes les options.', 'Please select all options.'));
        return;
      }

      // Button → loading state
      const originalText = btn.textContent || '';
      btn.disabled = true;
      btn.textContent = t(
        btn.dataset.labelAddingFr || 'Ajout en cours…',
        btn.dataset.labelAddingEn || 'Adding…'
      );

      try {
        // window.addToCart is defined in cart-functions.js (loaded globally)
        if (typeof window.addToCart !== 'function') {
          throw new Error('window.addToCart is not available. Make sure cart-functions.js is loaded.');
        }

        const cartData = await window.addToCart({ id: Number(variantId), quantity });

        // Button → success state
        btn.textContent = t(
          btn.dataset.labelAddedFr || 'Ajouté ✓',
          btn.dataset.labelAddedEn || 'Added ✓'
        );
        btn.classList.add('_btn-added');

        // Update any [data-cart-count] elements (e.g. header badge)
        if (typeof window.updateCartCount === 'function') {
          window.updateCartCount();
        }

        // Show add-to-cart confirmation popup
        if (typeof window.showAddToCartPopup === 'function') {
          const variantsScript = document.getElementById('product-variants-json');
          const variants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
          const currentVariant = variants.find((v) => String(v.id) === String(variantId)) || null;
          const popupOptions = currentVariant
            ? {
                variantTitle: currentVariant.title || '',
                price:
                  currentVariant.price != null && typeof formatMoney === 'function'
                    ? formatMoney(currentVariant.price)
                    : undefined,
              }
            : undefined;
          window.showAddToCartPopup(popupOptions);
        }

        // Dispatch cart:update event for theme components that listen
        // (matches the shape expected by CartUpdateEvent in events.js)
        const itemCount = cartData?.items_count ?? cartData?.item_count ?? null;
        const event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: 'product-add-form',
            data: {
              source: 'product-page',
              itemCount,
            },
          },
        });
        form.dispatchEvent(event);

        // Reset button after a short delay
        setTimeout(() => {
          btn.textContent = t(
            btn.dataset.labelAddFr || 'Ajouter au panier',
            btn.dataset.labelAddEn || 'Add to cart'
          );
          btn.classList.remove('_btn-added');
          btn.disabled = false;
        }, 2000);
      } catch (err) {
        console.error('[product-page] Add to cart failed:', err);

        btn.textContent = t(
          btn.dataset.labelErrorFr || 'Erreur — réessayer',
          btn.dataset.labelErrorEn || 'Error — try again'
        );
        btn.classList.add('_btn-error');

        setTimeout(() => {
          btn.textContent = originalText;
          btn.classList.remove('_btn-error');
          btn.disabled = false;
        }, 3000);
      }
    });
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    // Only run on product detail pages
    if (!document.getElementById('product-detail')) return;

    const updateGallery = initGallery();
    initQuantityStepper();
    initVariantSwitching(updateGallery);
    initAjaxAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
